/**
 * Bridge between AuthKit's React-based `useAuth()` hook and the axios
 * interceptors that run outside the React tree.
 *
 * The WorkosAuthBridge component blocks its children from rendering until
 * AuthKit finishes loading, then registers `getAccessToken` here. This
 * guarantees that by the time any API request fires, the token function
 * is available — no timing races.
 */

import sentryService from './sentry.service';

const WORKOS_SESSION_KEY = 'workos-session-active';
const WORKOS_EMAIL_KEY = 'workos-session-email';

let getAccessTokenFn: (() => Promise<string>) | null = null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let signOutFn: ((options?: any) => void | Promise<void>) | null = null;
// While true, hasWorkosSession() reports false even if the localStorage flag
// is still set. This lets us run the WorkOS server-side sign-out network call
// to completion *before* clearing the flag (so a sign-out failure doesn't
// leave the cookie alive while the client thinks it's logged out), while still
// blocking the sign-in route loader from bouncing the user back to /dashboard
// during the in-flight sign-out.
let signingOut = false;

export function registerWorkosAuth(
  getAccessToken: () => Promise<string>,
  // AuthKit's signOut has overloaded signatures that don't unify cleanly,
  // so we accept the function loosely and call it with the right shape internally.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  signOut: (...args: any[]) => any,
  email?: string | null,
): void {
  getAccessTokenFn = getAccessToken;
  signOutFn = signOut;
  localStorage.setItem(WORKOS_SESSION_KEY, 'true');
  // Persist the authenticated email so the AuthGuard's no_account bounce
  // (which happens after the WorkOS user/state has been cleared from React)
  // can still surface it on the interstitial banner.
  if (email) {
    localStorage.setItem(WORKOS_EMAIL_KEY, email);
  } else {
    localStorage.removeItem(WORKOS_EMAIL_KEY);
  }
}

export function clearWorkosAuth(): void {
  getAccessTokenFn = null;
  signOutFn = null;
  localStorage.removeItem(WORKOS_SESSION_KEY);
  localStorage.removeItem(WORKOS_EMAIL_KEY);
}

/**
 * Returns the email the user authenticated with via AuthKit, if a WorkOS
 * session is active. Read by the AuthGuard's no_account bounce to populate
 * the interstitial banner copy.
 */
export function getWorkosSessionEmail(): string | null {
  return localStorage.getItem(WORKOS_EMAIL_KEY);
}

/**
 * Signs out of the WorkOS session via AuthKit, clearing the session cookie
 * so the user is fully logged out (not just locally).
 *
 * The `signingOut` flag suppresses two concurrency hazards:
 * 1. The sign-in route loader can't bounce the user back to /dashboard while
 *    the server-side logout is mid-flight (hasWorkosSession() returns false).
 * 2. The WorkosAuthBridge's useLayoutEffect can't re-register the WorkOS
 *    singleton from a stale `user` value while logout is in progress —
 *    otherwise we get a re-register / re-clear oscillation and the screen
 *    flickers as the bridge fights the logout.
 *
 * Ordering: we always clear local state after the AuthKit call returns. AuthKit
 * follows a 302/307 redirect chain to invalidate the cookie, then probes
 * `/authenticate` which returns 400 because the session is now gone — AuthKit
 * surfaces that 400 as an error. From our perspective, the cookie has been
 * cleared regardless, so we treat the call as completing once it returns or
 * throws and unconditionally clear local state. We just report unexpected
 * errors to Sentry so genuine network failures are observable.
 *
 * Uses `navigate: false` so AuthKit clears its session server-side without
 * redirecting through WorkOS (which would redirect to whatever URL is
 * configured in the WorkOS dashboard, not necessarily the current environment).
 * We handle the redirect ourselves to stay on the correct origin.
 */
export async function workosSignOut(): Promise<void> {
  const localSignOut = signOutFn;
  if (!localSignOut) {
    clearWorkosAuth();
    return;
  }
  signingOut = true;
  try {
    await localSignOut({ navigate: false });
  } catch (error) {
    // NoSessionError means the server session was already gone — expected. All
    // other errors (including the post-logout 400 from /authenticate) are
    // reported but otherwise non-fatal: AuthKit's redirect chain has already
    // run by the time the call rejects, so the cookie is gone.
    if (!(error instanceof Error && error.name === 'NoSessionError')) {
      sentryService.captureException(error as Error, { context: 'workosSignOut' });
    }
  } finally {
    clearWorkosAuth();
    signingOut = false;
  }
}

/**
 * Returns a valid WorkOS access token if the user is authenticated via AuthKit.
 * Returns null if no WorkOS session is active (falls back to legacy auth).
 */
export async function getWorkosAccessToken(): Promise<string | null> {
  if (!getAccessTokenFn) return null;
  try {
    return await getAccessTokenFn();
  } catch {
    return null;
  }
}

/**
 * Synchronous check for router loaders that can't await promises.
 * Reads a localStorage flag set by the WorkosAuthBridge component.
 *
 * Returns false during an in-flight workosSignOut() so loaders don't bounce
 * the user back to /dashboard while the server-side sign-out is still running.
 */
export function hasWorkosSession(): boolean {
  if (signingOut) return false;
  return localStorage.getItem(WORKOS_SESSION_KEY) === 'true';
}

/**
 * True while a sign-out is in flight. The WorkosAuthBridge component reads
 * this to skip re-registration from a stale `useAuth().user` value during the
 * logout sequence.
 */
export function isSigningOut(): boolean {
  return signingOut;
}
